import React, { createContext, useState, useContext, useEffect } from 'react';

import api from '../api/api';
import { AuthContext } from './AuthContext';
import { DEFAULT_SETTINGS } from '../constants/appConstants';

const UserSettingsContext = createContext();

export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error('useUserSettings must be used within UserSettingsProvider');
  }
  return context;
};

export const UserSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    // Initialize from localStorage first
    const storedSettings = localStorage.getItem('userSettings');
    if (storedSettings) {
      try {
        return {
          ...DEFAULT_SETTINGS,
          ...JSON.parse(storedSettings)
        };
      } catch (error) {
        console.error('Error parsing stored settings:', error);
        return DEFAULT_SETTINGS;
      }
    }
    return DEFAULT_SETTINGS;
  });

  const [loading, setLoading] = useState(true);
  const { isAuthenticated, userType } = useContext(AuthContext);
  const isRegisteredUser = isAuthenticated && userType === 'registered';

  useEffect(() => {
    const fetchSettings = async () => {
      if (!isRegisteredUser) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/api/chat/user-settings`);
        const serverSettings = {
          ...DEFAULT_SETTINGS,
          ...response.data
        };

        setSettings(serverSettings);
        localStorage.setItem('userSettings', JSON.stringify(serverSettings));
      } catch (error) {
        console.error('Error fetching user settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [isRegisteredUser]);

  const updateSettings = async (newSettings) => {
    try {
      const updatedSettings = {
        ...settings,
        ...newSettings
      };

      // Always update localStorage
      localStorage.setItem('userSettings', JSON.stringify(updatedSettings));
      setSettings(updatedSettings);

      // Update server if user is registered
      if (isRegisteredUser) {
        await api.post(`${process.env.REACT_APP_API_BASE_URL}/api/chat/user-settings`, updatedSettings);
      }

      return true;
    } catch (error) {
      console.error('Error updating settings:', error);
      return false;
    }
  };

  return (
    <UserSettingsContext.Provider value={{
      settings,
      updateSettings,
      loading
    }}>
      {children}
    </UserSettingsContext.Provider>
  );
};